import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport, type UIMessage } from "ai";

import type { OutgoingIntakeAnswer } from "../../../api/types/outgoingIntake";
import { IntakeAssistantAPI, caseyChatUrl } from "../api/intakeAssistantApi";
import {
  EXTRACTION_PENDING,
  EXTRACTION_READY,
  isExtractionSettled,
  type CaseyAttachment,
  type CaseyDataParts,
  type CaseyProposal,
  type ProposalDecision,
  type SettledProposalDecision
} from "../types";
import { isAcceptedUpload, unsupportedUploadMessage } from "../uploadTypes";

export type CaseyUIMessage = UIMessage<never, CaseyDataParts>;

type UseCaseyChatOptions = {
  token: string;
  /**
   * The answers as they stand right now. Sent with every turn so Casey can see
   * what is already filled without the server holding session state.
   */
  answers: OutgoingIntakeAnswer[];
  /** Applies an accepted proposal to the form. */
  onAcceptProposal: (proposal: CaseyProposal) => void;
  /** The question the client is looking at, so Casey can answer "this one". */
  activeQuestionId?: string | null;
  preferredLanguage?: string | null;
};

/**
 * How long to keep asking whether a document has been read. Extraction is a
 * handful of seconds normally; past this the client is better served by Casey
 * reading it inline during the turn than by a spinner that never resolves.
 */
const ATTACHMENT_POLL_INTERVAL_MS = 1500;
const ATTACHMENT_POLL_TIMEOUT_MS = 90_000;

/**
 * How much longer to keep asking after extraction is done, waiting for the copy
 * of the document the client can be shown. The server writes it just after the
 * extraction result, so stopping the moment extraction lands would leave the
 * attachment looking unopenable for the rest of the session. Bounded because
 * the copy legitimately never arrives for a file Extend could not render.
 */
const PREVIEW_COPY_GRACE_MS = 20_000;

export function useCaseyChat({
  token,
  answers,
  onAcceptProposal,
  activeQuestionId,
  preferredLanguage
}: UseCaseyChatOptions) {
  const [attachments, setAttachments] = useState<CaseyAttachment[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [decisions, setDecisions] = useState<Record<string, ProposalDecision>>(
    {}
  );
  const [language, setLanguage] = useState<string | null>(
    preferredLanguage ?? null
  );
  // Starts true: the conversation is fetched on mount, and a panel that renders
  // its empty state first would tell a returning client they have never spoken.
  const [isRestoring, setIsRestoring] = useState<boolean>(Boolean(token));

  // The transport is built once, but each request must carry the answers and
  // attachments as of that moment. Refs give the request builder current values
  // without tearing down the chat on every keystroke in the form.
  const answersRef = useRef(answers);
  answersRef.current = answers;
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;
  const activeQuestionRef = useRef(activeQuestionId);
  activeQuestionRef.current = activeQuestionId;
  const languageRef = useRef(language);
  languageRef.current = language;

  const transport = useMemo(
    () =>
      new DefaultChatTransport<CaseyUIMessage>({
        api: caseyChatUrl(token),
        prepareSendMessagesRequest: ({ messages }) => ({
          body: {
            messages,
            answers: answersRef.current,
            attachment_ids: attachmentsRef.current.map((a) => a.attachment_id),
            active_question_id: activeQuestionRef.current ?? null,
            language: languageRef.current ?? null
          }
        })
      }),
    [token]
  );

  const chat = useChat<CaseyUIMessage>({
    transport,
    onData: (part) => {
      // Casey reports the language it settled on so later turns stay consistent
      // even after a reload.
      if (part.type === "data-language") {
        const next = part.data?.language;
        if (typeof next === "string" && next.trim()) setLanguage(next.trim());
      }
    }
  });

  // Read through a ref inside effects and callbacks: depending on `chat` itself
  // would retrigger them on every streamed token.
  const chatRef = useRef(chat);
  chatRef.current = chat;

  /** Whether this session has already contributed to the conversation. */
  const hasSentRef = useRef(false);

  useEffect(() => {
    if (!token) {
      setIsRestoring(false);
      return undefined;
    }

    let cancelled = false;
    const restore = async () => {
      try {
        const conversation = await IntakeAssistantAPI.fetchConversation(token);
        // A client who started typing before this landed has said something the
        // server's copy does not have yet; replacing it would erase their turn.
        if (cancelled || hasSentRef.current) return;
        if (conversation.messages?.length) {
          chatRef.current.setMessages(
            conversation.messages as unknown as CaseyUIMessage[]
          );
        }
        if (conversation.attachments?.length) {
          // Merged rather than assigned: a client who dropped a file in while
          // this was in flight would otherwise watch their chip disappear.
          setAttachments((prev) => {
            const alreadyHere = new Set(prev.map((a) => a.attachment_id));
            return [
              ...conversation.attachments.filter(
                (a) => !alreadyHere.has(a.attachment_id)
              ),
              ...prev
            ];
          });
        }
        if (conversation.decisions) setDecisions(conversation.decisions);
        if (conversation.language) setLanguage(conversation.language);
      } catch {
        // A conversation that cannot be read back is not worth an error in the
        // client's face: the panel still works, starting from empty.
      } finally {
        if (!cancelled) setIsRestoring(false);
      }
    };

    restore();
    return () => {
      cancelled = true;
    };
  }, [token]);

  /**
   * Remembers a settled decision, so a card the client has already ruled on is
   * not offered again on their next visit.
   *
   * Deliberately not awaited by its callers and deliberately quiet on failure:
   * the card has already moved, and the cost of losing this is one suggestion
   * reappearing rather than anything the client has to act on.
   */
  const persistDecisions = useCallback(
    (settled: Record<string, SettledProposalDecision>) => {
      if (!token || !Object.keys(settled).length) return;
      IntakeAssistantAPI.saveProposalDecisions(token, settled).catch(() => {
        // See above.
      });
    },
    [token]
  );

  /** Every proposal Casey has streamed this session, newest last. */
  const proposals = useMemo(() => {
    const byId = new Map<string, CaseyProposal>();
    for (const message of chat.messages) {
      for (const part of message.parts) {
        if (part.type === "data-proposals") {
          for (const proposal of part.data?.proposals ?? []) {
            byId.set(proposal.proposal_id, proposal);
          }
        }
      }
    }
    return Array.from(byId.values());
  }, [chat.messages]);

  /**
   * Applies one accepted suggestion to the form, without recording it.
   *
   * Returns the decision it settled on, or `null` when it did not settle, so a
   * bulk accept can record its outcome in one write instead of one per card.
   */
  const applyProposal = useCallback(
    async (proposalId: string): Promise<SettledProposalDecision | null> => {
      const proposal = proposals.find((p) => p.proposal_id === proposalId);
      if (!proposal) return null;

      // A document suggestion points at a chat attachment, which is not yet a
      // file on the intake. The server promotes it and hands back the real one,
      // rendered and review-checked, so the answer holds a file that behaves
      // like any the client uploaded themselves -- previewable included.
      if (proposal.attachment_id && proposal.kind === "document") {
        setDecisions((prev) => ({ ...prev, [proposalId]: "applying" }));
        try {
          const file = await IntakeAssistantAPI.attachToIntake(
            token,
            proposal.attachment_id
          );
          onAcceptProposal({ ...proposal, file });
          setDecisions((prev) => ({ ...prev, [proposalId]: "accepted" }));
          return "accepted";
        } catch {
          // Back to pending: the card offers the click again rather than
          // claiming a file was added that never arrived.
          setDecisions((prev) => ({ ...prev, [proposalId]: "pending" }));
          setUploadError("That file could not be added. Please try again.");
          return null;
        }
      }

      onAcceptProposal(proposal);
      setDecisions((prev) => ({ ...prev, [proposalId]: "accepted" }));
      return "accepted";
    },
    [proposals, onAcceptProposal, token]
  );

  const acceptProposal = useCallback(
    async (proposalId: string) => {
      const settled = await applyProposal(proposalId);
      if (settled) persistDecisions({ [proposalId]: settled });
    },
    [applyProposal, persistDecisions]
  );

  const rejectProposal = useCallback(
    (proposalId: string) => {
      setDecisions((prev) => ({ ...prev, [proposalId]: "rejected" }));
      persistDecisions({ [proposalId]: "rejected" });
    },
    [persistDecisions]
  );

  const acceptAll = useCallback(
    async (proposalIds: string[]) => {
      // One at a time: accepting a document is a request, and the intake's
      // upload rate limit is shared with the chat panel's own uploads.
      const settled: Record<string, SettledProposalDecision> = {};
      await proposalIds.reduce<Promise<void>>(
        (chain, id) =>
          chain.then(async () => {
            const decision = await applyProposal(id);
            if (decision) settled[id] = decision;
          }),
        Promise.resolve()
      );
      persistDecisions(settled);
    },
    [applyProposal, persistDecisions]
  );

  const pollTimers = useRef<Record<string, number>>({});

  useEffect(
    () => () => {
      for (const timer of Object.values(pollTimers.current)) {
        window.clearTimeout(timer);
      }
    },
    []
  );

  /**
   * Watches a just-uploaded document until extraction resolves, so the UI can
   * say "read your passport" instead of leaving the client guessing.
   */
  const pollAttachment = useCallback(
    (attachmentId: string, startedAt = Date.now()) => {
      let settledAt: number | null = null;
      const tick = async () => {
        try {
          const latest = await IntakeAssistantAPI.getAttachment(
            token,
            attachmentId
          );
          setAttachments((prev) =>
            prev.map((a) => (a.attachment_id === attachmentId ? latest : a))
          );
          if (isExtractionSettled(latest.extraction_status)) {
            if (settledAt === null) settledAt = Date.now();
            // Extraction being over is not the end of the story: the document
            // only becomes previewable once its copy has been written.
            const stillWaiting =
              latest.extraction_status === EXTRACTION_READY &&
              !latest.has_citation_pdf &&
              Date.now() - settledAt < PREVIEW_COPY_GRACE_MS;
            if (!stillWaiting) return;
          }
        } catch {
          // A failed poll is not worth surfacing; the next one may succeed and
          // Casey can always read the document during the turn instead.
        }
        if (Date.now() - startedAt > ATTACHMENT_POLL_TIMEOUT_MS) return;
        pollTimers.current[attachmentId] = window.setTimeout(
          tick,
          ATTACHMENT_POLL_INTERVAL_MS
        );
      };
      pollTimers.current[attachmentId] = window.setTimeout(
        tick,
        ATTACHMENT_POLL_INTERVAL_MS
      );
    },
    [token]
  );

  const uploadAttachment = useCallback(
    async (file: File) => {
      setUploadError(null);
      if (!isAcceptedUpload(file)) {
        setUploadError(unsupportedUploadMessage(file));
        return null;
      }
      // Show the file immediately; a document upload is the slowest thing the
      // client does here and silence reads as breakage.
      const placeholder: CaseyAttachment = {
        attachment_id: `pending-${file.name}-${Date.now()}`,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        extraction_status: EXTRACTION_PENDING
      };
      setAttachments((prev) => [...prev, placeholder]);
      try {
        const attachment = await IntakeAssistantAPI.uploadAttachment(
          token,
          file
        );
        setAttachments((prev) =>
          prev.map((a) =>
            a.attachment_id === placeholder.attachment_id ? attachment : a
          )
        );
        pollAttachment(attachment.attachment_id);
        return attachment;
      } catch (error) {
        setAttachments((prev) =>
          prev.filter((a) => a.attachment_id !== placeholder.attachment_id)
        );
        setUploadError(
          error instanceof Error
            ? error.message
            : "That file could not be uploaded."
        );
        return null;
      }
    },
    [token, pollAttachment]
  );

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      hasSentRef.current = true;
      chat.sendMessage({ text: trimmed });
    },
    [chat]
  );

  return {
    messages: chat.messages,
    status: chat.status,
    error: chat.error,
    clearError: chat.clearError,
    sendMessage,
    stop: chat.stop,
    isStreaming: chat.status === "streaming" || chat.status === "submitted",
    isRestoring,
    attachments,
    uploadAttachment,
    uploadError,
    proposals,
    decisions,
    acceptProposal,
    rejectProposal,
    acceptAll,
    language
  };
}
